import { useState } from 'react';
import { useTracking } from 'react-tracking';
import If from '@/components/If';
import {
  Text,
  Button,
  Grid,
  useTheme,
  usePreventWindowScroll,
} from '@simplywallst/ui-core';
import { toCurrency } from '@/utilities/formatting';
import ActionSheet, {
  Header,
  Panel,
  Sheet,
  Background,
} from '@/components/ActionSheet';

import { FairValue } from './FairValue';
import { ValuationHistogram } from './ValuationHistogram';
import {
  ValuationContainer,
  Wrapper,
  ModalContent,
  ModalFooter,
  TruncatedValuation,
} from './styled';
import type { Valuation, Company } from '../../hooks/useNarrative/types';
import { useHtmlReactParser } from '../../hooks';

interface Props {
  valuation: Valuation;
  company: Company;
  authorImageUrl: string;
  announcementCount: number;
}

const PreventWindowScroll = () => {
  usePreventWindowScroll();
  return null;
};

export const AuthorValuation = ({
  valuation,
  company,
  authorImageUrl,
  announcementCount,
}: Props) => {
  const { trackEvent } = useTracking();
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const content = useHtmlReactParser({ html: valuation.contentHtml });
  return (
    <ValuationContainer gap="16px">
      <FairValue
        fairValue={valuation.fairValue}
        currencyISO={company.data.currencyInfo.trading_item_currency_iso}
        valuationByLine={valuation.valuationByLine}
      />
      <Wrapper gap="16px">
        <ValuationHistogram
          sharePrice={company.analysisValue.lastSharePrice as number}
          authorFairValue={valuation.fairValue}
          authorImageUrl={authorImageUrl}
          currencyISO={company.data.currencyInfo.trading_item_currency_iso}
        />
        <Text typography="tiny" color="mediumGray" textAlign="center">
          Price at publication{' '}
          {toCurrency(
            valuation.sharePriceAtPublish,
            2,
            company.data.currencyInfo.trading_item_currency_iso
          )}
        </Text>
        <TruncatedValuation data-cy-id="narrative-author-valuation-content">
          {content}
        </TruncatedValuation>
        <Button
          styleType="loudLarge"
          data-cy-id="narrative-author-valuation-see-more"
          onClick={() => {
            trackEvent({
              action: 'view',
              modifier: 'valuation',
              companyId: company.id,
              uniqueSymbol: company.uniqueSymbol,
              announcementCount,
            });
            setIsModalOpen(true);
          }}
        >
          See more
        </Button>
      </Wrapper>
      <If match={isModalOpen}>
        {() => (
          <ActionSheet open={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <PreventWindowScroll />
            <Background onClick={() => setIsModalOpen(false)} />
            <Sheet>
              <Grid
                data-cy-id="narrative-author-valuation-modal"
                templateRows={`calc(40px + ${theme.safeAreaInsetTop}) auto`}
              >
                <Panel templateRows="max-content 1fr" row="2">
                  <Header>
                    <Text
                      typography="baseStrong"
                      color="white"
                      data-cy-id="narrative-author-valuation-modal-heading"
                    >
                      My Valuation
                    </Text>
                  </Header>
                  <ModalContent data-cy-id="narrative-author-valuation-modal-content">
                    {content}
                  </ModalContent>
                  <ModalFooter
                    templateColumns="max-content"
                    justifyContent="end"
                  >
                    <Button
                      styleType="loudLarge"
                      onClick={() => setIsModalOpen(false)}
                      data-cy-id="narrative-author-valuation-modal-close-button"
                    >
                      Close
                    </Button>
                  </ModalFooter>
                </Panel>
              </Grid>
            </Sheet>
          </ActionSheet>
        )}
      </If>
    </ValuationContainer>
  );
};
